import { IUser } from '@monetize/types/app';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSetAtom } from 'jotai';
import { isAxiosError } from '../api/axios';
import { doGetTenantUser } from '../api/usersService';
import { ROUTES } from '../constants';
import { currentTenantState } from '../store/global.store';
import { forgetLastTenant } from '../store/store.utils';

/** Delay before the single retry of a failed tenant-user fetch */
export const TENANT_USER_RETRY_DELAY_MS = 750;

/** 4xx statuses that describe a transient condition rather than a rejected request */
const TRANSIENT_4XX_STATUSES = [408, 425, 429];

/**
 * A 4xx (other than the transient ones above) means the user genuinely cannot
 * use this tenant (removed, never a member, bad id), so retrying is pointless.
 * Anything else (network blip, timeout, rate limit, 5xx) gets one retry so a
 * transient failure does not bounce the user out of their tenant.
 */
const isRetryableError = (err: unknown) => {
  const status = isAxiosError(err) ? err.response?.status : undefined;
  return !status || status >= 500 || TRANSIENT_4XX_STATUSES.includes(status);
};

const wait = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

const fetchTenantUser = async (
  tenantId: string,
  userId: string,
): Promise<IUser> => {
  try {
    return await doGetTenantUser(tenantId, userId);
  } catch (err) {
    if (!isRetryableError(err)) {
      throw err;
    }
    await wait(TENANT_USER_RETRY_DELAY_MS);
    return doGetTenantUser(tenantId, userId);
  }
};

const useCurrentUser = (
  isTokenRefreshed: boolean,
  tenantId?: string,
  userId?: string,
) => {
  const navigate = useNavigate();
  // user fetched from /tenants/{id}/users/{id}
  const [currentTenantUser, setCurrentTenantUser] = useState<null | IUser>(
    null,
  );
  const setCurrentTenant = useSetAtom(currentTenantState);
  const [error, setError] = useState<any>();

  const currentTenantUserHasRole = useCallback(
    (routeRoles: string[]) => {
      // If routeRoles contains 'ALL', allow access for any user
      if (routeRoles.includes('ALL')) {
        return true;
      }

      return (
        currentTenantUser?.roles?.some((role) =>
          routeRoles.includes(role.name),
        ) || false
      );
    },
    [currentTenantUser],
  );

  const currentTenantUserHasSpecificRole = useCallback(
    (routeRole: string) => {
      return (
        currentTenantUser?.roles?.some((role) => role.name === routeRole) ||
        false
      );
    },
    [currentTenantUser],
  );

  useEffect(() => {
    // Ignore a response that lands after the tenant or user changed underneath it
    let isStale = false;

    const loadData = async (tenantIdP: string, userIdP: string) => {
      try {
        const tenantUser = await fetchTenantUser(tenantIdP, userIdP);
        if (isStale) {
          return;
        }
        setCurrentTenantUser(tenantUser);
        setError(undefined);
      } catch (err) {
        if (isStale) {
          return;
        }
        setError(err);
        setCurrentTenantUser(null);
        // Remove current tenant selected (and stop auto-restoring it) and
        // navigate to switch the tenant
        forgetLastTenant(tenantIdP);
        setCurrentTenant(null);
        navigate(ROUTES.ROOT);
      }
    };
    if (isTokenRefreshed && userId && tenantId) {
      loadData(tenantId, userId);
    } else {
      setCurrentTenantUser(null);
    }

    return () => {
      isStale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenRefreshed, userId, tenantId, setCurrentTenant]);

  return {
    error,
    currentTenantUser,
    currentTenantUserHasRole,
    currentTenantUserHasSpecificRole,
  };
};

export default useCurrentUser;
